import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Observable, map, startWith } from 'rxjs';

// Material imports
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Services
import { UsersService } from '../../service/entity-services/users.service';
import { EntityFormDataService } from '../../service/form-data/entity-form-data.service';
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { SiteService } from '../../service/entity-services/site.service';
import { LegalEntityService } from '../../service/entity-services/legal-entity.service';
import { PermissionsService } from '../../service/permissions/permissions.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';
import { ContractService } from '../../service/entity-services/contract.service';
import { PermissionsUtilityService } from '../../service/permissions/permissions-utility.service';

@Component({
  selector: 'app-permissions',
  templateUrl: './permissions.component.html',
  styleUrls: ['./permissions.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatDividerModule,
    MatAutocompleteModule,
    MatInputModule,
    MatDialogModule,
    MatProgressSpinnerModule
  ],
  providers: [EntityFormDataService]
})
export class PermissionsComponent implements OnInit {
  private usersService = inject(UsersService);
  private entityFormData = inject(EntityFormDataService);
  private commonSheetsService = inject(CommonSheetsService);
  private siteService = inject(SiteService);
  private legalEntityService = inject(LegalEntityService);
  private permissionsService = inject(PermissionsService);
  readonly permissionsStore = inject(PermissionsStoreService);
  private contractService = inject(ContractService);
  private permissionsUtility = inject(PermissionsUtilityService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  // Form controls
  selectedUserControl = new FormControl<any>(null);
  selectedRoleControl = new FormControl<string>('');
  selectedDepartmentsControl = new FormControl<string[]>([]);
  selectedSitesControl = new FormControl<any[]>([]);
  selectedLegalEntitiesControl = new FormControl<string[]>([]);
  selectedContractTypesControl = new FormControl<string[]>([]);
  selectedDatabaseOrganizationsControl = new FormControl<string[]>([]);

  // Autocomplete input controls
  departmentInputControl = new FormControl<string>('');
  siteInputControl = new FormControl<string>('');
  legalEntityInputControl = new FormControl<string>('');
  contractTypeInputControl = new FormControl<string>('');
  databaseOrgInputControl = new FormControl<string>('');

  // Data lists
  listOfUsers = signal<any[]>([]);
  roleList = signal<string[]>([]);
  allDepartments = signal<string[]>([]);
  allSites = signal<any[]>([]);
  allLegalEntities = signal<any[]>([]);
  contractTypes = signal<string[]>([]);
  databaseOrganizations = signal<string[]>([]);

  // Filtered lists for autocomplete
  filteredDepartments$!: Observable<string[]>;
  filteredSites$!: Observable<any[]>;
  filteredLegalEntities$!: Observable<any[]>;
  filteredContractTypes$!: Observable<string[]>;
  filteredDatabaseOrgs$!: Observable<string[]>;

  // UI state
  // Only admins (Administrator / System Administrator) may view/change roles &
  // permissions. Drives the template gate (editor vs denial card). The backend is
  // the authoritative boundary on the write; this is the UI gate.
  get canManageUsers(): boolean {
    return this.permissionsStore.isAdmin();
  }
  userRole: string | null = null;
  saving = false;

  // Contract count signals
  allContractsCount = signal<number>(0);
  visibleContractsCount = signal<number>(0);
  calculatingCount = signal<boolean>(false);

  async ngOnInit() {
    // Admin-only screen (changing roles/permissions). Non-admins who reach this via
    // a direct URL get the denial template and we load NOTHING on their behalf — no
    // user roster, no sites/legal-entities. The backend is the real boundary on the
    // write; this is the UI gate. The menu link is also hidden for non-admins.
    if (!this.permissionsStore.isAdmin()) {
      return;
    }

    // Get user roles
    this.roleList.set(this.entityFormData.getUserRoles());

    // Load users
    this.usersService.allUsers$.subscribe(users => {
      //console.log('PermissionsComponent - users loaded:', users.length);
      this.listOfUsers.set(users);
    });

    // Trigger users refresh
    this.usersService.refreshAllUsersRecords();

    // Load departments
    this.commonSheetsService.getDepartments().subscribe(departments => {
      this.allDepartments.set(departments);
    });

    // Load contract types
    this.commonSheetsService.getContractTypes().subscribe(types => {
      this.contractTypes.set(types);
    });

    // Load database organizations
    this.commonSheetsService.getDatabaseOrganizations().subscribe(orgs => {
      this.databaseOrganizations.set(orgs);
    });

    // Load sites
    this.siteService.allSites$.subscribe(sites => {
      this.allSites.set(sites);
    });

    // Load legal entities
    this.legalEntityService.allLegalEntities$.subscribe(entities => {
      this.allLegalEntities.set(entities);
    });

    // Subscribe to user selection changes
    this.selectedUserControl.valueChanges.subscribe(user => {
      this.userSelected(user);
    });

    // Setup autocomplete filtering
    this.setupAutocompleteFilters();

    // Subscribe to all contracts count
    this.contractService.allContracts$.subscribe(contracts => {
      this.allContractsCount.set(contracts.length);
    });

    // Subscribe to permission form changes to update visible contracts count
    this.selectedDepartmentsControl.valueChanges.subscribe(() => this.updateVisibleContractsCountFromForm());
    this.selectedSitesControl.valueChanges.subscribe(() => this.updateVisibleContractsCountFromForm());
    this.selectedLegalEntitiesControl.valueChanges.subscribe(() => this.updateVisibleContractsCountFromForm());
    this.selectedContractTypesControl.valueChanges.subscribe(() => this.updateVisibleContractsCountFromForm());
    this.selectedDatabaseOrganizationsControl.valueChanges.subscribe(() => this.updateVisibleContractsCountFromForm());
  }

  private setupAutocompleteFilters() {
    // Filter departments
    this.filteredDepartments$ = this.departmentInputControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterStringArray(value || '', this.allDepartments()))
    );

    // Filter sites
    this.filteredSites$ = this.siteInputControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterSites(value || ''))
    );

    // Filter legal entities
    this.filteredLegalEntities$ = this.legalEntityInputControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterLegalEntities(value || ''))
    );

    // Filter contract types
    this.filteredContractTypes$ = this.contractTypeInputControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterStringArray(value || '', this.contractTypes()))
    );

    // Filter database organizations
    this.filteredDatabaseOrgs$ = this.databaseOrgInputControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterStringArray(value || '', this.databaseOrganizations()))
    );
  }

  private filterStringArray(value: string, array: string[]): string[] {
    const filterValue = value.toLowerCase();
    return array.filter(item => item.toLowerCase().includes(filterValue));
  }

  private filterSites(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.allSites().filter(site =>
      site.name?.toLowerCase().includes(filterValue)
    );
  }

  private filterLegalEntities(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.allLegalEntities().filter(entity =>
      entity.legalEntityName?.toLowerCase().includes(filterValue)
    );
  }

  async userSelected(user: any) {
    if (!user) {
      this.clearPermissions();
      return;
    }

    // Fetch user permissions from API
    const userId = user.userId || user.email;
    if (!userId) {
      console.error('User has no userId or email');
      this.clearPermissions();
      return;
    }

    try {
      const permissions = await this.permissionsService.getUserPermissionsById(userId);
      //console.log('PermissionsComponent - permissions for user:', permissions);

      if (permissions) {
        // Populate form with fetched permissions
        this.selectedRoleControl.setValue(permissions.role || '');
        this.selectedDepartmentsControl.setValue(permissions.departments || []);
        this.selectedContractTypesControl.setValue(permissions.contractTypes || []);
        this.selectedDatabaseOrganizationsControl.setValue(permissions.databaseOrganizations || []);

        // Sites - convert to full objects if needed
        if (permissions.sites && Array.isArray(permissions.sites)) {
          const siteObjects = permissions.sites.map(site => {
            if (typeof site === 'object' && site.siteNum) {
              return site;
            }
            // If it's just a siteNum string, find the full object
            return this.allSites().find(s => s.siteNum === site) || site;
          });
          this.selectedSitesControl.setValue(siteObjects);
        } else {
          this.selectedSitesControl.setValue([]);
        }

        // Legal Entities - should be array of legalEntityNums
        if (permissions.legalEntities && Array.isArray(permissions.legalEntities)) {
          const legalEntityNums = permissions.legalEntities.map(le => {
            return typeof le === 'object' ? (le as any).legalEntityNum : le;
          }).filter((num): num is string => typeof num === 'string');
          this.selectedLegalEntitiesControl.setValue(legalEntityNums);
        } else {
          this.selectedLegalEntitiesControl.setValue([]);
        }

        // Calculate visible contracts count for this user
        this.updateVisibleContractsCount(permissions);
      } else {
        // User has no permissions set yet
        this.clearPermissions();
        this.visibleContractsCount.set(0);
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
      this.clearPermissions();
    }
  }

  clearPermissions() {
    this.selectedRoleControl.setValue('');
    this.selectedDepartmentsControl.setValue([]);
    this.selectedSitesControl.setValue([]);
    this.selectedLegalEntitiesControl.setValue([]);
    this.selectedContractTypesControl.setValue([]);
    this.selectedDatabaseOrganizationsControl.setValue([]);
  }

  /**
   * Updates the visible contracts count based on the given permissions.
   * This calculates how many contracts the user would be able to see.
   */
  updateVisibleContractsCount(permissions: any) {
    this.calculatingCount.set(true);

    // Get the selected user's email for ownership checking. The control's value is
    // the whole user OBJECT, so pull the email string off it (not the object).
    const selectedUserEmail = this.selectedUserControl.value?.email || null;

    // Mirror the runtime visibility logic in contract.service so this preview tells
    // the truth. A viewer-like user (role 'Viewer' OR no role assigned yet) is
    // restricted to contracts they OWN (+ any assigned metadata) — the "no limits =
    // see all contracts" shortcut applies only to privileged roles. Without this,
    // the preview claims a Viewer can see every contract, contradicting what they
    // actually see at login.
    const role = (permissions?.role ?? '').toString().toLowerCase().trim();
    const viewerLike = role === '' || role === 'viewer';

    // Use setTimeout to ensure spinner shows before heavy calculation
    setTimeout(() => {
      this.contractService.allContracts$.subscribe(allContracts => {
        let count: number;
        if (viewerLike) {
          count = this.permissionsUtility.filterContractsForViewer(allContracts as any, permissions, selectedUserEmail).length;
        } else if (!permissions || this.hasNoPermissionLimits(permissions)) {
          // Privileged role with no limits - can see all contracts
          count = allContracts.length;
        } else {
          // Privileged role with limits - filter (with ownership override)
          count = this.permissionsUtility.filterContracts(allContracts as any, permissions, selectedUserEmail).length;
        }
        this.visibleContractsCount.set(count);
        this.calculatingCount.set(false);
      }).unsubscribe(); // Immediately unsubscribe after getting the count
    }, 50);
  }

  /**
   * Checks if the user has no permission limits.
   */
  private hasNoPermissionLimits(permissions: any): boolean {
    return (!permissions.legalEntities || permissions.legalEntities.length === 0) &&
           (!permissions.contractTypes || permissions.contractTypes.length === 0) &&
           (!permissions.departments || permissions.departments.length === 0) &&
           (!permissions.sites || permissions.sites.length === 0) &&
           (!permissions.databaseOrganizations || permissions.databaseOrganizations.length === 0);
  }

  /**
   * Updates the visible contracts count based on current form values.
   * This is called whenever any permission control changes.
   */
  updateVisibleContractsCountFromForm() {
    // Only calculate if a user is selected
    if (!this.selectedUserControl.value) {
      return;
    }

    // Build permissions object from current form values
    const permissions = {
      role: this.selectedRoleControl.value || '',
      departments: this.selectedDepartmentsControl.value || [],
      sites: this.selectedSitesControl.value || [],
      legalEntities: this.selectedLegalEntitiesControl.value || [],
      contractTypes: this.selectedContractTypesControl.value || [],
      databaseOrganizations: this.selectedDatabaseOrganizationsControl.value || []
    };

    this.updateVisibleContractsCount(permissions);
  }

  async saveUserPermissions() {
    // Defense in depth: only admins may write roles/permissions. The UI already
    // hides this screen from non-admins, but guard the write too. Backend is the
    // authoritative boundary.
    if (!this.permissionsStore.isAdmin()) {
      this.snackBar.open('You do not have permission to change roles.', 'Close', { duration: 4000 });
      return;
    }

    const selectedUser = this.selectedUserControl.value;

    if (!selectedUser) {
      this.snackBar.open('Please select a user first', 'Close', { duration: 3000 });
      return;
    }

    const userId = selectedUser.userId || selectedUser.email;
    if (!userId) {
      console.error('User has no userId or email');
      this.snackBar.open('Error: User has no valid ID', 'Close', { duration: 5000 });
      return;
    }

    this.saving = true;

    try {
      // Build permissions object
      const permissions = {
        role: this.selectedRoleControl.value || '',
        departments: this.selectedDepartmentsControl.value || [],
        sites: this.selectedSitesControl.value || [],
        legalEntities: this.selectedLegalEntitiesControl.value || [],
        contractTypes: this.selectedContractTypesControl.value || [],
        databaseOrganizations: this.selectedDatabaseOrganizationsControl.value || []
      };

      // Save to API
      await this.permissionsService.updateUserPermissions(userId, permissions);

      // Show success message
      this.snackBar.open('Permissions saved successfully!', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });

      // Reset the form and reload data
      this.selectedUserControl.setValue(null);
      this.clearPermissions();
      this.saving = false;

      // Refresh user list in case new user was created
      this.usersService.refreshAllUsersRecords();

    } catch (error) {
      console.error('Error saving permissions:', error);
      this.saving = false;
      this.snackBar.open('Error saving permissions. Please try again.', 'Close', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
    }
  }

  getUserDisplayName(user: any): string {
    return user?.shortFriendlyName || `${user?.family_name}, ${user?.given_name}` || '';
  }

  getSiteDisplayName(site: any): string {
    return site?.name || '';
  }

  getLegalEntityDisplayName(entity: any): string {
    return entity?.legalEntityName || '';
  }

  // Autocomplete selection handlers
  selectDepartment(department: string) {
    const currentDepartments = this.selectedDepartmentsControl.value || [];
    if (!currentDepartments.includes(department)) {
      this.selectedDepartmentsControl.setValue([...currentDepartments, department]);
    }
    this.departmentInputControl.setValue('');
  }

  selectSite(site: any) {
    const currentSites = this.selectedSitesControl.value || [];
    if (!currentSites.find(s => s.siteNum === site.siteNum)) {
      this.selectedSitesControl.setValue([...currentSites, site]);
    }
    this.siteInputControl.setValue('');
  }

  selectLegalEntity(entityNum: string) {
    const currentEntities = this.selectedLegalEntitiesControl.value || [];
    if (!currentEntities.includes(entityNum)) {
      this.selectedLegalEntitiesControl.setValue([...currentEntities, entityNum]);
    }
    this.legalEntityInputControl.setValue('');
  }

  selectContractType(type: string) {
    const currentTypes = this.selectedContractTypesControl.value || [];
    if (!currentTypes.includes(type)) {
      this.selectedContractTypesControl.setValue([...currentTypes, type]);
    }
    this.contractTypeInputControl.setValue('');
  }

  selectDatabaseOrg(org: string) {
    const currentOrgs = this.selectedDatabaseOrganizationsControl.value || [];
    if (!currentOrgs.includes(org)) {
      this.selectedDatabaseOrganizationsControl.setValue([...currentOrgs, org]);
    }
    this.databaseOrgInputControl.setValue('');
  }

  // Chip removal handlers
  removeDepartment(department: string) {
    const currentDepartments = this.selectedDepartmentsControl.value || [];
    this.selectedDepartmentsControl.setValue(
      currentDepartments.filter(d => d !== department)
    );
  }

  removeSite(site: any) {
    const currentSites = this.selectedSitesControl.value || [];
    this.selectedSitesControl.setValue(
      currentSites.filter(s => s.siteNum !== site.siteNum)
    );
  }

  removeLegalEntity(entityNum: string) {
    const currentEntities = this.selectedLegalEntitiesControl.value || [];
    this.selectedLegalEntitiesControl.setValue(
      currentEntities.filter(e => e !== entityNum)
    );
  }

  removeContractType(type: string) {
    const currentTypes = this.selectedContractTypesControl.value || [];
    this.selectedContractTypesControl.setValue(
      currentTypes.filter(t => t !== type)
    );
  }

  removeDatabaseOrg(org: string) {
    const currentOrgs = this.selectedDatabaseOrganizationsControl.value || [];
    this.selectedDatabaseOrganizationsControl.setValue(
      currentOrgs.filter(o => o !== org)
    );
  }
}
